import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { api } from "../api/client.ts";
import { toast } from "./feedback.tsx";
import { useDebounced } from "./ui.tsx";
import { useUnsavedChanges } from "./unsaved.ts";

export interface Audience {
  listIds: string[];
  dispositions: string[];
  excludeListIds: string[];
  excludeDispositions: string[];
  /** Smart Sending window in hours; null is off. */
  smartSendingHours: number | null;
}

export const EMPTY_AUDIENCE: Audience = {
  listIds: [],
  dispositions: [],
  excludeListIds: [],
  excludeDispositions: [],
  smartSendingHours: 16,
};

interface ListOption {
  id: string;
  brand_id: string;
  name: string;
  members: number;
  isSegment?: boolean;
}
interface DispositionOption {
  value: string;
  n: number;
  label: string;
}

/** Lists, segments and Pulse dispositions: everything an audience can name. */
function useAudienceOptions() {
  const lists = useQuery({
    queryKey: ["lists"],
    queryFn: () => api.get<ListOption[]>("/audience/lists"),
  });
  const facets = useQuery({
    queryKey: ["pulse-facets"],
    queryFn: () => api.get<{ dispositions?: DispositionOption[] }>("/audience/pulse-facets"),
    staleTime: 5 * 60_000,
  });
  return { lists: lists.data ?? [], dispositions: facets.data?.dispositions ?? [] };
}

/** Whether the audience names anyone to send to; the API refuses it otherwise. */
export function hasRecipients(a: Audience): boolean {
  return a.listIds.length > 0 || a.dispositions.length > 0;
}

/**
 * "Send to" and "Don't send to", each any mix of lists, segments and Pulse
 * dispositions, plus Smart Sending. Someone in a "send to" entry gets it once
 * however many they are in; anyone in a "don't send to" entry doesn't, whatever
 * the rest say.
 */
export function AudiencePicker({
  brandId,
  value,
  onChange,
  disabled,
  campaignId = null,
}: {
  brandId: string;
  value: Audience;
  onChange: (next: Audience) => void;
  disabled?: boolean | undefined;
  /** The broadcast being edited, whose own sends Smart Sending ignores. */
  campaignId?: string | null;
}) {
  const options = useAudienceOptions();
  const brandLists = options.lists.filter((l) => l.brand_id === brandId);
  const otherLists = options.lists.filter((l) => l.brand_id !== brandId);
  const used = new Set([
    ...value.listIds,
    ...value.excludeListIds,
    ...value.dispositions,
    ...value.excludeDispositions,
  ]);

  return (
    <div className="audience">
      <AudienceSide
        id="aud-include"
        label="Send to"
        lists={value.listIds}
        dispositions={value.dispositions}
        options={{ ...options, lists: brandLists, otherLists: [] }}
        used={used}
        disabled={disabled}
        onChange={(listIds, dispositions) => onChange({ ...value, listIds, dispositions })}
        empty={
          brandLists.length === 0
            ? "No lists for this brand yet. A list belongs to one brand, because consent and unsubscribes are per brand."
            : undefined
        }
      />
      <AudienceSide
        id="aud-exclude"
        label="Don't send to"
        lists={value.excludeListIds}
        dispositions={value.excludeDispositions}
        // Leaving people out is safe whoever's list it is.
        options={{ ...options, lists: brandLists, otherLists }}
        used={used}
        disabled={disabled}
        onChange={(excludeListIds, excludeDispositions) =>
          onChange({ ...value, excludeListIds, excludeDispositions })
        }
      />
      <SmartSending
        hours={value.smartSendingHours}
        disabled={disabled}
        onChange={(smartSendingHours) => onChange({ ...value, smartSendingHours })}
      />
      {brandId && <AudienceCount brandId={brandId} audience={value} campaignId={campaignId} />}
    </div>
  );
}

interface SideOptions {
  lists: ListOption[];
  otherLists: ListOption[];
  dispositions: DispositionOption[];
}

function AudienceSide({
  id,
  label,
  lists,
  dispositions,
  options,
  used,
  disabled,
  onChange,
  empty,
}: {
  id: string;
  label: string;
  lists: string[];
  dispositions: string[];
  options: SideOptions;
  used: Set<string>;
  disabled?: boolean | undefined;
  onChange: (lists: string[], dispositions: string[]) => void;
  empty?: string | undefined;
}) {
  const allLists = [...options.lists, ...options.otherLists];
  const listName = (listId: string) => {
    const l = allLists.find((x) => x.id === listId);
    return l ? `${l.isSegment ? "Segment" : "List"}: ${l.name}` : "A list";
  };
  const dispositionName = (v: string) =>
    `Disposition: ${options.dispositions.find((d) => d.value === v)?.label ?? v}`;

  const add = (picked: string) => {
    const v = picked.slice(2);
    if (picked.startsWith("l:")) onChange([...lists, v], dispositions);
    else onChange(lists, [...dispositions, v]);
  };

  return (
    <div className="field">
      <label htmlFor={id}>{label}</label>
      {(lists.length > 0 || dispositions.length > 0) && (
        <div className="aud-chips">
          {lists.map((l) => (
            <AudienceChip
              key={l}
              label={listName(l)}
              disabled={disabled}
              onRemove={() =>
                onChange(
                  lists.filter((x) => x !== l),
                  dispositions,
                )
              }
            />
          ))}
          {dispositions.map((d) => (
            <AudienceChip
              key={d}
              label={dispositionName(d)}
              disabled={disabled}
              onRemove={() =>
                onChange(
                  lists,
                  dispositions.filter((x) => x !== d),
                )
              }
            />
          ))}
        </div>
      )}
      <select
        id={id}
        value=""
        disabled={disabled}
        onChange={(e) => {
          if (e.target.value) add(e.target.value);
        }}
      >
        <option value="">Add a list, segment or disposition…</option>
        <SideOptionGroups options={options} used={used} />
      </select>
      {empty && <div className="hint">{empty}</div>}
    </div>
  );
}

function SideOptionGroups({ options, used }: { options: SideOptions; used: Set<string> }) {
  const group = (title: string, rows: ListOption[]) => {
    const free = rows.filter((l) => !used.has(l.id));
    if (free.length === 0) return null;
    return (
      <optgroup label={title}>
        {free.map((l) => (
          <option key={l.id} value={`l:${l.id}`}>
            {l.name} ({l.members.toLocaleString()})
          </option>
        ))}
      </optgroup>
    );
  };
  const freeDispositions = options.dispositions.filter((d) => !used.has(d.value));
  return (
    <>
      {group(
        "Lists",
        options.lists.filter((l) => !l.isSegment),
      )}
      {group(
        "Segments",
        options.lists.filter((l) => l.isSegment),
      )}
      {group("Other brands' lists", options.otherLists)}
      {freeDispositions.length > 0 && (
        <optgroup label="Pulse dispositions">
          {freeDispositions.map((d) => (
            <option key={d.value} value={`d:${d.value}`}>
              {d.label} ({d.n.toLocaleString()})
            </option>
          ))}
        </optgroup>
      )}
    </>
  );
}

function AudienceChip({
  label,
  onRemove,
  disabled,
}: {
  label: string;
  onRemove: () => void;
  disabled?: boolean | undefined;
}) {
  return (
    <span className="aud-chip">
      {label}
      {!disabled && (
        <button type="button" aria-label={`Remove ${label}`} onClick={onRemove}>
          ×
        </button>
      )}
    </span>
  );
}

/** Skips anyone another campaign mailed recently, so nobody gets two in a row. */
function SmartSending({
  hours,
  onChange,
  disabled,
}: {
  hours: number | null;
  onChange: (hours: number | null) => void;
  disabled?: boolean | undefined;
}) {
  return (
    <div className="local-delivery">
      <label className="check">
        <input
          type="checkbox"
          checked={hours !== null}
          disabled={disabled}
          onChange={(e) => onChange(e.target.checked ? 16 : null)}
        />{" "}
        Smart Sending
      </label>
      {hours !== null && (
        <div className="local-delivery-fields">
          Skip anyone mailed in the past
          <input
            type="number"
            aria-label="Smart Sending hours"
            min={1}
            max={168}
            value={hours}
            disabled={disabled}
            style={{ width: 72 }}
            onChange={(e) => {
              const n = Math.round(Number(e.target.value));
              if (n >= 1 && n <= 168) onChange(n);
            }}
          />
          hours
        </div>
      )}
      <div className="hint">
        {hours !== null
          ? "Counts any other campaign or sequence. Checked again as each message goes out."
          : "Off: everyone in the audience is mailed, however recently they heard from you."}
      </div>
    </div>
  );
}

/**
 * A saved broadcast's audience. Editable until it goes out; after that the
 * sends are queued and changing it would describe something that didn't happen.
 */
export function SavedAudience({
  campaignId,
  brandId,
  editable,
}: {
  campaignId: string;
  brandId: string;
  editable: boolean;
}) {
  const qc = useQueryClient();
  const saved = useQuery({
    queryKey: ["broadcast-audience", campaignId],
    queryFn: () => api.get<Audience>(`/broadcasts/${campaignId}/audience`),
  });
  const [draft, setDraft] = useState<Audience | null>(null);
  const value = draft ?? saved.data;
  const save = useMutation({
    mutationFn: (a: Audience) => api.put(`/broadcasts/${campaignId}/audience`, a),
    onSuccess: () => {
      setDraft(null);
      toast("Audience saved");
      void qc.invalidateQueries({ queryKey: ["broadcast-audience", campaignId] });
      void qc.invalidateQueries({ queryKey: ["broadcast-recipients", campaignId] });
      void qc.invalidateQueries({ queryKey: ["broadcast", campaignId] });
    },
    onError: (err: Error) => toast(err.message, "bad"),
  });
  useUnsavedChanges(draft !== null);
  if (!value) return null;

  return (
    <div style={{ marginBottom: 12 }}>
      <AudiencePicker
        brandId={brandId}
        campaignId={campaignId}
        value={value}
        onChange={setDraft}
        disabled={!editable || save.isPending}
      />
      {draft && (
        <div className="toolbar" style={{ marginBottom: 0 }}>
          <button
            type="button"
            className="btn small"
            disabled={save.isPending || !hasRecipients(draft)}
            onClick={() => save.mutate(draft)}
          >
            Save audience
          </button>
          <button type="button" className="btn small ghost" onClick={() => setDraft(null)}>
            Discard
          </button>
          {!hasRecipients(draft) && (
            <span className="hint">Pick at least one list, segment or disposition to send to.</span>
          )}
        </div>
      )}
    </div>
  );
}

interface Counts {
  members: number;
  eligible: number;
  excluded: number;
  recentlyMailed: number;
}

/**
 * Who the audience as picked would reach, recounted as it changes — so an
 * exclusion that swallows everyone shows as 0 before anything is saved.
 */
function AudienceCount({
  brandId,
  audience,
  campaignId,
}: {
  brandId: string;
  audience: Audience;
  campaignId: string | null;
}) {
  const settled = useDebounced(audience, 400);
  const count = useQuery({
    queryKey: ["audience-preview", brandId, campaignId, settled],
    queryFn: () =>
      api.post<Counts>("/broadcasts/audience-preview", { brandId, campaignId, ...settled }),
    enabled: hasRecipients(settled),
    placeholderData: (previous) => previous,
  });
  if (!hasRecipients(audience)) {
    return <p className="aud-count hint">Pick who to send to and the count shows here.</p>;
  }
  if (count.error) return <p className="aud-count error">{(count.error as Error).message}</p>;
  const n = count.data;
  if (!n) return <p className="aud-count hint">Counting…</p>;
  const left = [
    n.excluded > 0 && `${n.excluded.toLocaleString()} suppressed or unsubscribed`,
    n.recentlyMailed > 0 && `${n.recentlyMailed.toLocaleString()} skipped by Smart Sending`,
  ].filter(Boolean);
  return (
    <p
      className={`aud-count${n.eligible === 0 ? " empty" : ""}`}
      style={{ opacity: count.isFetching ? 0.6 : 1 }}
    >
      <strong>{n.eligible.toLocaleString()}</strong> {n.eligible === 1 ? "person" : "people"} would
      receive this
      {n.members !== n.eligible && <> of {n.members.toLocaleString()} in the audience</>}.
      {left.length > 0 && <span className="hint"> {left.join(", ")}.</span>}
      {n.eligible === 0 && n.members === 0 && (
        <span className="hint"> Nobody matches — check what you're leaving out.</span>
      )}
    </p>
  );
}
